import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { TextareaWithConfirm, RadioOptionsWithConfirm, CardContainer, CardWithModal } from 'shared-components';

import QuestionPage from '../../../templates/question';
import OtherVisas from './visas/other';
import VisaRefusals from './visas/refusals';
import NonUKVisaRefusals from './visas/nonUkRefusals';

import { Q } from '../../../../store/student/types';
import { ConfigField, IStudentOtherVisa, IStudentVisaRefusal, IStudentNonUKVisaRefusal } from '../../../../types';
import { titleAppend } from '../../../../utilities/head';
import { useDispatch, useSelector } from 'react-redux';
import * as StudentActions from '../../../../store/student/actions';
import * as HistoryActions from '../../../../store/student/history/actions';
import {
  createNavigateForwardAction,
  createNavigateBackwardAction,
  createNavigateToAction
} from '../../../../middleware/actions';
import { Pages } from '../../../../store/student/history/types';
import { toggleTaskMenu } from '../../../../store/ui/actions';
import { ApplicationState } from 'frontend/student/src/store';

const toBool = (val?: boolean) => (val === undefined ? true : val);

const useData = () => {
  const sectionTitle = useSelector((state: ApplicationState) => state.config.Checklist.History.shortTitle);
  const config = useSelector((state: ApplicationState) => state.config);

  const {
    PrevUKOtherVisa: prevUKOtherVisaConfig,
    UKLeaveToRemain: ukLeaveToRemainConfig,
    PrevRejections: prevRejectionsConfig,
    PrevRejectionReason: prevRejectionReasonConfig,
    PrevVisaRefusal: prevVisaRefusalConfig,
    PrevNonUKVisaRefusal: prevNonUKVisaRefusalConfig
  }: {
    PrevUKOtherVisa: ConfigField;
    UKLeaveToRemain: ConfigField;
    PrevRejections: ConfigField;
    PrevRejectionReason: ConfigField;
    PrevVisaRefusal: ConfigField;
    PrevNonUKVisaRefusal: ConfigField;
  } = useSelector((state: ApplicationState) => state.config.Checklist.History.Page2);

  const {
    hasOtherUkVisas = undefined,
    otherUkVisas = [],
    hasLeaveToRemain = undefined,
    hasBorderRefusal = undefined,
    borderRefusalReason = '',
    hasUkVisaRefusals = undefined,
    ukVisaRefusals = [],
    hasOtherVisaRefusals = undefined,
    otherVisaRefusals = []
  } = useSelector((state: ApplicationState) => state.student.history);

  const allVisasComplete = (amount: number): boolean => {
    let complete = (hasOtherUkVisas === true && amount > 0) || hasOtherUkVisas === false;
    for (let i = 0; i < amount; i++) {
      complete = complete && otherUkVisas[i].visaType !== '' && otherUkVisas[i].year !== '';
    }
    return complete;
  };

  const allRefusalsComplete = (amount: number): boolean => {
    let complete = (hasUkVisaRefusals === true && amount > 0) || hasUkVisaRefusals === false;
    for (let i = 0; i < amount; i++) {
      complete = complete && ukVisaRefusals[i].visaType !== '' && ukVisaRefusals[i].year !== '';
    }
    return complete;
  };

  const allNonUKRefusalsComplete = (amount: number): boolean => {
    let complete = (hasOtherVisaRefusals === true && amount > 0) || hasOtherVisaRefusals === false;
    for (let i = 0; i < amount; i++) {
      complete =
        complete &&
        otherVisaRefusals[i].country !== '' &&
        otherVisaRefusals[i].isStudentVisa !== undefined &&
        otherVisaRefusals[i].year !== '';
    }
    return complete;
  };

  const prevUKOtherVisaVisible = toBool(prevUKOtherVisaConfig?.visible);
  const ukLeaveToRemainVisible = toBool(ukLeaveToRemainConfig?.visible);
  const prevRejectionsVisible = toBool(prevRejectionsConfig?.visible);
  const prevRejectionReasonVisible = toBool(prevRejectionReasonConfig?.visible) && hasBorderRefusal === true;
  const prevVisaRefusalVisible = toBool(prevVisaRefusalConfig?.visible);
  const prevNonUKVisaRefusalVisible = toBool(prevNonUKVisaRefusalConfig?.visible);

  const prevUKOtherVisaMandatory = toBool(prevUKOtherVisaConfig?.mandatory);
  const ukLeaveToRemainMandatory = toBool(ukLeaveToRemainConfig?.mandatory);
  const prevRejectionsMandatory = toBool(prevRejectionsConfig?.mandatory);
  const prevRejectionReasonMandatory = toBool(prevRejectionReasonConfig?.mandatory);
  const prevVisaRefusalMandatory = toBool(prevVisaRefusalConfig?.mandatory);
  const prevNonUKVisaRefusalMandatory = toBool(prevNonUKVisaRefusalConfig?.mandatory);

  // prettier-ignore
  const complete =
    (!prevUKOtherVisaVisible || !prevUKOtherVisaMandatory || hasOtherUkVisas !== undefined)  &&
    (!ukLeaveToRemainVisible || !ukLeaveToRemainMandatory || hasLeaveToRemain !== undefined)  &&
    (!prevRejectionsVisible || !prevRejectionsMandatory || hasBorderRefusal !== undefined)  &&
    (!prevRejectionReasonVisible || !prevRejectionReasonMandatory || borderRefusalReason !== '')  &&
    (!prevVisaRefusalVisible || !prevVisaRefusalMandatory || ukVisaRefusals !== undefined)  &&
    (!prevNonUKVisaRefusalVisible || !prevNonUKVisaRefusalMandatory || hasOtherVisaRefusals !== undefined)  &&
    allVisasComplete(Number(otherUkVisas.length)) &&
    allRefusalsComplete(Number(ukVisaRefusals.length)) &&
    allNonUKRefusalsComplete(Number(otherVisaRefusals.length));

  return {
    sectionTitle,
    complete,
    currentStep: 2,
    steps: 3,
    hasOtherUkVisas,
    prevUKOtherVisaVisible,
    prevUKOtherVisaConfig,
    otherUkVisas,
    hasLeaveToRemain,
    ukLeaveToRemainVisible,
    ukLeaveToRemainConfig,
    hasBorderRefusal,
    prevRejectionsVisible,
    prevRejectionsConfig,
    borderRefusalReason,
    prevRejectionReasonVisible,
    prevRejectionReasonConfig,
    hasUkVisaRefusals,
    prevVisaRefusalVisible,
    prevVisaRefusalConfig,
    ukVisaRefusals,
    hasOtherVisaRefusals,
    prevNonUKVisaRefusalVisible,
    prevNonUKVisaRefusalConfig,
    otherVisaRefusals,
    config
  };
};

const HistoryQPage2 = () => {
  const data = useData();
  const {
    sectionTitle,
    complete,
    currentStep,
    steps,
    hasOtherUkVisas,
    prevUKOtherVisaVisible,
    prevUKOtherVisaConfig,
    hasLeaveToRemain,
    ukLeaveToRemainVisible,
    ukLeaveToRemainConfig,
    hasBorderRefusal,
    prevRejectionsVisible,
    prevRejectionsConfig,
    borderRefusalReason,
    prevRejectionReasonVisible,
    prevRejectionReasonConfig,
    hasUkVisaRefusals,
    prevVisaRefusalVisible,
    prevVisaRefusalConfig,
    hasOtherVisaRefusals,
    prevNonUKVisaRefusalVisible,
    prevNonUKVisaRefusalConfig,
    config
  } = data;
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = config => {
    dispatch(StudentActions.saveHistory(Pages.PAGE2, config));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value) =>
    dispatch(HistoryActions.createUpdateHistoryFieldAction(fieldName, value));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const [showModalOtherVisas, setShowModalOtherVisas] = useState([...data.otherUkVisas.map(_ => false), false]);
  const [otherUkVisas, setOtherUkVisas] = useState([
    ...(data.otherUkVisas?.length ? data.otherUkVisas : []),
    { visaType: '', year: '' }
  ]);

  useEffect(() => {
    if (data.otherUkVisas.length > 0) setOtherUkVisas([...data.otherUkVisas, { visaType: '', year: '' }]);
  }, [data.otherUkVisas]);

  const [showModalVisasRefusals, setShowModalVisasRefusals] = useState([...data.ukVisaRefusals.map(_ => false), false]);
  const [ukVisaRefusals, setUkVisasRefusals] = useState([
    ...(data.ukVisaRefusals?.length ? data.ukVisaRefusals : []),
    { visaType: '', year: '' }
  ]);

  useEffect(() => {
    if (data.ukVisaRefusals.length > 0) setUkVisasRefusals([...data.ukVisaRefusals, { visaType: '', year: '' }]);
  }, [data.ukVisaRefusals]);

  const [showModalOtherVisaRefusals, setShowModalOtherVisaRefusals] = useState([
    ...data.otherVisaRefusals.map(_ => false),
    false
  ]);
  const [otherVisaRefusals, setOtherVisaRefusals] = useState([
    ...(data.otherVisaRefusals?.length ? data.otherVisaRefusals : []),
    { country: '', year: '', isStudentVisa: undefined }
  ]);

  useEffect(() => {
    if (data.otherVisaRefusals.length > 0)
      setOtherVisaRefusals([...data.otherVisaRefusals, { country: '', year: '', isStudentVisa: undefined }]);
  }, [data.otherVisaRefusals]);

  const toggleModal = (index: number, showModalsArray: Array<boolean>, cb: (data) => void): void => {
    const newShowModal = [...showModalsArray];
    newShowModal[index] = !showModalsArray[index];
    cb(newShowModal);
  };

  const deleteVisa = (
    index: number,
    arrayOfValues: Array<any>,
    target: Q,
    cdForArray: (data) => void,
    cdForModals: (data) => void,
    additionalElement: IStudentNonUKVisaRefusal | IStudentOtherVisa | IStudentVisaRefusal
  ): void => {
    const newArrayOfValues = [...arrayOfValues];
    onChange(target, [...newArrayOfValues.slice(0, index), ...newArrayOfValues.slice(index + 1)]);

    cdForArray([...[...newArrayOfValues.slice(0, index), ...newArrayOfValues.slice(index + 1)], additionalElement]);
    cdForModals([
      ...[...newArrayOfValues.slice(0, index), ...newArrayOfValues.slice(index + 1)].map(_ => false),
      false
    ]);
  };

  const handleYesNoKeyPress = (target: Q, event, question): void => {
    switch (event.key.toLowerCase()) {
      case 'y':
        onChange(target, true);
        break;
      case 'n':
        onChange(target, false);
        break;
      case 'enter':
      case 'space':
        onChange(target, question.char === 'Y' ? true : false);
        break;
    }
  };

  const handleYesNoClick = (target: Q, question): void => {
    onChange(target, question.char === 'Y' ? true : false);
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={() => onNext(config)}
      onSkip={onSkip}
      onPrevious={onPrevious}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      {prevUKOtherVisaVisible && (
        <RadioOptionsWithConfirm
          key={Q.PREV_UK_OTHER_VISA}
          id={Q.PREV_UK_OTHER_VISA}
          label={'Have you been granted any other visas (non-student) for the UK?'}
          inputHelperText={prevUKOtherVisaConfig?.helpText}
          onClick={question => handleYesNoClick(Q.PREV_UK_OTHER_VISA, question)}
          onKeyDown={(event, question) => handleYesNoKeyPress(Q.PREV_UK_OTHER_VISA, event, question)}
          isInline={true}
          questionMaxWidth={220}
          maxQuestions={2}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasOtherUkVisas === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasOtherUkVisas === false
            }
          ]}
        />
      )}
      {hasOtherUkVisas && (
        <CardContainer title={`Other UK Visas`}>
          {otherUkVisas.map((visa: IStudentOtherVisa, index: number) => (
            <CardWithModal
              key={index}
              id={`other-visa-${index}`}
              index={index}
              modalTitle={'Add an Other UK Visa'}
              additionalDataSelectorName={'other-visa'}
              cardTitle={visa.visaType}
              cardItems={[
                {
                  label: 'Year',
                  value: visa.year
                }
              ]}
              onRemove={(index: number) =>
                deleteVisa(
                  index,
                  [...data.otherUkVisas],
                  Q.PREV_UK_OTHER_VISAS,
                  setOtherUkVisas,
                  setShowModalOtherVisas,
                  { visaType: '', year: '' }
                )
              }
              addButtonLabel={`Add Other UK VISA`}
              addAnotherButtonLabel={`I have another visa to add`}
              hasAddMoreButton={visa.visaType === '' && visa.year === ''}
              showModal={showModalOtherVisas[index]}
              toggleModal={(value: number) => toggleModal(value, showModalOtherVisas, setShowModalOtherVisas)}
            >
              <OtherVisas
                index={index}
                visaType={visa.visaType}
                year={visa.year}
                onSaveAndClose={(visa: IStudentOtherVisa) => {
                  const stateOtherUkVisas = [...data.otherUkVisas];
                  stateOtherUkVisas.pop();
                  const newOtherUkVisas = [...data.otherUkVisas];
                  newOtherUkVisas[index] = {
                    ...visa
                  };
                  onChange(Q.PREV_UK_OTHER_VISAS, newOtherUkVisas);
                  if (JSON.stringify(stateOtherUkVisas) !== JSON.stringify(newOtherUkVisas)) {
                    setOtherUkVisas([...(newOtherUkVisas?.length ? newOtherUkVisas : []), { visaType: '', year: '' }]);
                    setShowModalOtherVisas([...newOtherUkVisas.map(_ => false), false]);
                  }
                  toggleModal(index, showModalOtherVisas, setShowModalOtherVisas);
                }}
              />
            </CardWithModal>
          ))}
        </CardContainer>
      )}

      {ukLeaveToRemainVisible && (
        <RadioOptionsWithConfirm
          key={Q.UK_LEAVE_TO_REMAIN}
          id={Q.UK_LEAVE_TO_REMAIN}
          value={hasLeaveToRemain}
          label={'Have you made an application to the Home Office to remain in the UK in the last 10 years?'}
          inputHelperText={ukLeaveToRemainConfig?.helpText}
          onClick={question => handleYesNoClick(Q.UK_LEAVE_TO_REMAIN, question)}
          onKeyDown={(event, question) => handleYesNoKeyPress(Q.UK_LEAVE_TO_REMAIN, event, question)}
          isInline={true}
          questionMaxWidth={220}
          maxQuestions={2}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasLeaveToRemain === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasLeaveToRemain === false
            }
          ]}
        />
      )}
      {prevRejectionsVisible && (
        <RadioOptionsWithConfirm
          key={Q.PREVIOUS_VISA_REJECTION}
          id={Q.PREVIOUS_VISA_REJECTION}
          label={'Have you been refused entry to the UK at the border in the last 10 years?'}
          inputHelperText={prevRejectionsConfig?.helpText}
          onClick={question => handleYesNoClick(Q.PREVIOUS_VISA_REJECTION, question)}
          onKeyDown={(event, question) => handleYesNoKeyPress(Q.PREVIOUS_VISA_REJECTION, event, question)}
          isInline={true}
          questionMaxWidth={220}
          maxQuestions={2}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasBorderRefusal === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasBorderRefusal === false
            }
          ]}
        />
      )}
      {prevRejectionReasonVisible && (
        <TextareaWithConfirm
          key={Q.PREVIOUS_VISA_REJECTION_REASON}
          id={Q.PREVIOUS_VISA_REJECTION_REASON}
          value={borderRefusalReason}
          label={`Please provide more information`}
          inputHelperText={prevRejectionReasonConfig?.helpText}
          onChange={evt => onChange(Q.PREVIOUS_VISA_REJECTION_REASON, evt.currentTarget.value)}
        />
      )}
      {prevVisaRefusalVisible && (
        <RadioOptionsWithConfirm
          key={Q.PREV_VISA_REFUSAL}
          id={Q.PREV_VISA_REFUSAL}
          label={'Have you ever previously received a visa refusal when attempting to travel to the UK?'}
          inputHelperText={prevVisaRefusalConfig?.helpText}
          onClick={question => handleYesNoClick(Q.PREV_VISA_REFUSAL, question)}
          onKeyDown={(event, question) => handleYesNoKeyPress(Q.PREV_VISA_REFUSAL, event, question)}
          isInline={true}
          questionMaxWidth={220}
          maxQuestions={2}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasUkVisaRefusals === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasUkVisaRefusals === false
            }
          ]}
        />
      )}

      {hasUkVisaRefusals && (
        <CardContainer title={`UK Visa Refusals`}>
          {ukVisaRefusals.map((visa: IStudentVisaRefusal, index: number) => (
            <CardWithModal
              key={index}
              id={`visa-refusal-${index}`}
              index={index}
              modalTitle={'Add a UK Visa Refusal'}
              additionalDataSelectorName={'visa-refusal'}
              cardTitle={`${visa.visaType} Visa Refusal`}
              cardItems={[
                {
                  label: 'Year',
                  value: visa.year
                }
              ]}
              onRemove={(index: number) =>
                deleteVisa(
                  index,
                  [...data.ukVisaRefusals],
                  Q.PREV_VISA_REFUSALS,
                  setUkVisasRefusals,
                  setShowModalVisasRefusals,
                  { visaType: '', year: '' }
                )
              }
              addButtonLabel={`Add UK Visa Refusal`}
              addAnotherButtonLabel={`I have another visa refusal to add`}
              hasAddMoreButton={visa.visaType === '' && visa.year === ''}
              toggleModal={(value: number) => toggleModal(value, showModalVisasRefusals, setShowModalVisasRefusals)}
              showModal={showModalVisasRefusals[index]}
            >
              <VisaRefusals
                index={index}
                visaType={visa.visaType}
                year={visa.year}
                onSaveAndClose={(visa: IStudentVisaRefusal) => {
                  const stateUkVisaRefusals = [...ukVisaRefusals];
                  stateUkVisaRefusals.pop();
                  const newUkVisaRefusals = [...data.ukVisaRefusals];
                  newUkVisaRefusals[index] = {
                    ...visa
                  };
                  onChange(Q.PREV_VISA_REFUSALS, newUkVisaRefusals);
                  if (JSON.stringify(stateUkVisaRefusals) !== JSON.stringify(newUkVisaRefusals)) {
                    setUkVisasRefusals([
                      ...(newUkVisaRefusals?.length ? newUkVisaRefusals : []),
                      { visaType: '', year: '' }
                    ]);
                    setShowModalVisasRefusals([...newUkVisaRefusals.map(_ => false), false]);
                  }
                  toggleModal(index, showModalVisasRefusals, setShowModalVisasRefusals);
                }}
              />
            </CardWithModal>
          ))}
        </CardContainer>
      )}

      {prevNonUKVisaRefusalVisible && (
        <RadioOptionsWithConfirm
          key={Q.PREV_NON_UK_VISA_REFUSAL}
          id={Q.PREV_NON_UK_VISA_REFUSAL}
          label={'Have you received a visa refusal from any other country (non-UK) in the last 10 years?'}
          inputHelperText={prevNonUKVisaRefusalConfig?.helpText}
          onClick={question => handleYesNoClick(Q.PREV_NON_UK_VISA_REFUSAL, question)}
          onKeyDown={(event, question) => handleYesNoKeyPress(Q.PREV_NON_UK_VISA_REFUSAL, event, question)}
          isInline={true}
          questionMaxWidth={220}
          maxQuestions={2}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasOtherVisaRefusals === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasOtherVisaRefusals === false
            }
          ]}
        />
      )}

      {hasOtherVisaRefusals && (
        <CardContainer title={`Non-UK Visa Refusals`}>
          {otherVisaRefusals.map((visa: IStudentNonUKVisaRefusal, index: number) => (
            <CardWithModal
              key={index}
              id={`visa-refusal-${index}`}
              index={index}
              additionalDataSelectorName={'non-uk-visa-refusal'}
              modalTitle={'Add a Non-UK Visa Refusal'}
              cardTitle={`${visa.country} Visa Refusal`}
              cardItems={[
                {
                  label: 'Year',
                  value: visa.year
                },
                {
                  label: 'Student Visa',
                  value: visa.isStudentVisa ? 'Yes' : 'No'
                }
              ]}
              addButtonLabel={`Add Previous Non-UK Visa Refusal`}
              addAnotherButtonLabel={`I have another visa refusal to add`}
              hasAddMoreButton={visa.country === '' && visa.year === '' && visa.isStudentVisa === undefined}
              onRemove={(index: number) =>
                deleteVisa(
                  index,
                  [...data.otherVisaRefusals],
                  Q.PREV_NON_UK_VISA_REFUSALS,
                  setOtherVisaRefusals,
                  setShowModalOtherVisaRefusals,
                  { country: '', year: '', isStudentVisa: undefined }
                )
              }
              toggleModal={(value: number) =>
                toggleModal(value, showModalOtherVisaRefusals, setShowModalOtherVisaRefusals)
              }
              showModal={showModalOtherVisaRefusals[index]}
            >
              <NonUKVisaRefusals
                index={index}
                country={visa.country}
                year={visa.year}
                isStudentVisa={visa.isStudentVisa}
                onSaveAndClose={(visa: IStudentNonUKVisaRefusal) => {
                  const stateOtherVisaRefusals = [...otherVisaRefusals];
                  stateOtherVisaRefusals.pop();
                  const newOtherVisaRefusals = [...data.otherVisaRefusals];
                  newOtherVisaRefusals[index] = {
                    ...visa
                  };
                  onChange(Q.PREV_NON_UK_VISA_REFUSALS, newOtherVisaRefusals);
                  if (JSON.stringify(stateOtherVisaRefusals) !== JSON.stringify(newOtherVisaRefusals)) {
                    setOtherVisaRefusals([
                      ...(newOtherVisaRefusals?.length ? newOtherVisaRefusals : []),
                      {
                        country: '',
                        year: '',
                        isStudentVisa: undefined
                      }
                    ]);
                    setShowModalOtherVisaRefusals([...newOtherVisaRefusals.map(_ => false), false]);
                  }
                  toggleModal(index, showModalOtherVisaRefusals, setShowModalOtherVisaRefusals);
                }}
              />
            </CardWithModal>
          ))}
        </CardContainer>
      )}
    </QuestionPage>
  );
};

export default HistoryQPage2;
